import { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Panel, PageHead, Label, StatusBadge, Empty } from "@/components/common";
import { useApi, errMsg, inr, fmtDate } from "@/lib/api";

const WithdrawForm = ({ me, onDone }) => {
  const api = useApi();
  const [amount, setAmount] = useState("");
  const [upi, setUpi] = useState(me.user.upi_id || "");
  const [busy, setBusy] = useState(false);
  const active = me.user.status === "active";
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api("post", "/me/withdrawals", { amount: Number(amount), upi_id: upi }); toast.success("Withdrawal requested"); setAmount(""); onDone(); }
    catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  return (
    <Panel>
      <Label>Request payout</Label>
      <p className="mt-3 font-display text-4xl font-extrabold text-white" data-testid="withdraw-balance">{inr(me.user.wallet)}</p>
      <p className="text-sm text-slate-500">Available · minimum {inr(me.settings.min_withdrawal)}</p>
      <form onSubmit={submit} className="mt-6 space-y-3">
        <Input data-testid="withdraw-amount-input" type="number" min={me.settings.min_withdrawal} max={me.user.wallet} placeholder="Amount (₹)" value={amount} onChange={(e) => setAmount(e.target.value)} />
        <Input data-testid="withdraw-upi-input" placeholder="Your UPI ID (name@bank)" value={upi} onChange={(e) => setUpi(e.target.value)} />
        <Button data-testid="withdraw-submit-btn" type="submit" disabled={!active || busy || !amount || !upi} className="h-11 w-full rounded-full font-semibold">{busy ? "Requesting…" : "Request withdrawal"}</Button>
        {!active && <p className="text-xs text-amber-400">Activate your ID to withdraw.</p>}
      </form>
    </Panel>
  );
};

export default function Withdraw() {
  const api = useApi();
  const { me, refresh } = useOutletContext();
  const [rows, setRows] = useState(null);
  const load = useCallback(() => api("get", "/me/withdrawals").then(setRows), [api]);
  useEffect(() => { load(); }, [load]);
  return (
    <div data-testid="withdraw-page">
      <PageHead label="Wallet" title="Withdraw" />
      <div className="grid gap-6 lg:grid-cols-3">
        <WithdrawForm me={me} onDone={() => { load(); refresh(); }} />
        <Panel className="lg:col-span-2">
          <Label>History</Label>
          <div className="mt-4">
            {!rows ? <p className="text-sm text-slate-500">Loading…</p> : rows.length === 0 ? <Empty testId="withdraw-empty" text="No withdrawal requests yet." /> : (
              <Table data-testid="withdraw-history-table">
                <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>UPI ID</TableHead><TableHead>Amount</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                <TableBody>
                  {rows.map((w) => (
                    <TableRow key={w.id} data-testid={`withdraw-row-${w.id}`}>
                      <TableCell className="text-xs">{fmtDate(w.created_at)}</TableCell>
                      <TableCell className="font-mono text-xs">{w.upi_id}</TableCell>
                      <TableCell className="font-mono text-white">{inr(w.amount)}</TableCell>
                      <TableCell><StatusBadge status={w.status} />{w.reason && <p className="mt-1 text-xs text-red-400">{w.reason}</p>}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </Panel>
      </div>
    </div>
  );
}
